import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { api } from "../lib/api";
import { regexParse } from "../lib/regexParser";
import { SAMPLE_WA } from "../lib/samples";
import { Sparkles, Wand2, Eraser, Loader2 } from "lucide-react";

export function ParserPanel({ label, setLabel }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const applyParsed = (p) => {
    setLabel((prev) => ({
      ...prev,
      recipient_name: p.recipient_name || "",
      phone: p.phone || "",
      address: p.address || "",
      courier: p.courier || prev.courier || "",
      items: p.items || "",
      notes: p.notes || "",
      code: p.code || "",
      cod: p.cod || prev.cod || "",
    }));
  };

  const handleRegex = () => {
    if (!text.trim()) return toast.error("Tempel dulu teks pesanannya ya");
    applyParsed(regexParse(text));
    toast.success("Alamat terdeteksi (mode cepat)");
  };

  const handleAI = async () => {
    if (!text.trim()) return toast.error("Tempel dulu teks pesanannya ya");
    setLoading(true);
    try {
      const { data } = await api.post("/parse", { text });
      applyParsed(data);
      toast.success("Alamat berhasil diparse oleh AI!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI parser gagal, coba mode cepat");
    } finally {
      setLoading(false);
    }
  };

  const field = (key, labelText, props = {}) => (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{labelText}</Label>
      <Input
        data-testid={`parsed-field-${key}`}
        value={label[key] || ""}
        onChange={(e) => setLabel((p) => ({ ...p, [key]: e.target.value }))}
        {...props}
      />
    </div>
  );

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2 font-bold text-slate-900">
          <Sparkles size={18} className="text-sky-600" /> Tempel Teks Pesanan WhatsApp
        </div>
        <Textarea
          data-testid="wa-parser-input-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Contoh:\nNama: Budi Santoso\nNo HP: 081234567890\nAlamat: Jl. Merdeka No 45, Jakarta Pusat\nEkspedisi: J&T\nBarang: 2x Kaos Polos L"}
          className="min-h-[150px] font-mono text-sm"
        />
        <div className="flex flex-wrap gap-2">
          {SAMPLE_WA.map((s, i) => (
            <Button key={i} data-testid={`btn-load-sample-wa-${i + 1}`} variant="outline" size="sm"
              onClick={() => setText(s.text)} className="text-xs">
              {s.label}
            </Button>
          ))}
          {text && (
            <Button variant="ghost" size="sm" onClick={() => setText("")} className="text-xs text-slate-500">
              <Eraser size={14} className="mr-1" /> Bersihkan
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="btn-parse-ai" onClick={handleAI} disabled={loading}
            className="flex-1 bg-sky-600 hover:bg-sky-700 gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            Parse dengan AI
          </Button>
          <Button data-testid="btn-parse-regex" onClick={handleRegex} variant="outline" className="gap-2">
            Mode Cepat
          </Button>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="font-bold text-slate-900 text-sm">Data Penerima (bisa diedit)</div>
        {field("recipient_name", "Nama Penerima")}
        <div className="grid grid-cols-2 gap-3">
          {field("phone", "No HP")}
          {field("code", "Kode Pesanan / Resi")}
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Alamat Lengkap</Label>
          <Textarea data-testid="parsed-field-address" value={label.address || ""}
            onChange={(e) => setLabel((p) => ({ ...p, address: e.target.value }))} className="min-h-[70px]" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {field("items", "Isi Paket")}
          {field("cod", "Nilai COD (Rp)", { type: "number" })}
        </div>
      </Card>
    </div>
  );
}
